import React, { useState, useMemo } from 'react';
import { Message, Button } from 'semantic-ui-react';
import { generateNudges } from '../../utils/nudgeCalculation';

// Per-user key: a shared browser must not let user B inherit user A's
// dismissals (matches the earnedAchievements_${userId} convention).
const dismissStorageKey = (userId) => `dashboard_nudge_dismissed_${userId || 'anon'}`;

const getDismissedNudges = (userId) => {
    try {
        const stored = JSON.parse(localStorage.getItem(dismissStorageKey(userId)) || '{}');
        const now = Date.now();
        // Clean expired (14 days)
        const active = {};
        Object.entries(stored).forEach(([id, ts]) => {
            if (now - ts < 14 * 24 * 60 * 60 * 1000) active[id] = ts;
        });
        return active;
    } catch { return {}; }
};

const dismissNudge = (userId, id) => {
    // The write is guarded like the read: blocked storage (Safari private
    // mode) must not throw inside the dismiss click handler.
    try {
        const dismissed = getDismissedNudges(userId);
        dismissed[id] = Date.now();
        localStorage.setItem(dismissStorageKey(userId), JSON.stringify(dismissed));
    } catch { /* storage blocked — dismissal lasts for this render only */ }
};

const typeColorMap = {
    warning: { bg: '#fff3cd', border: '#ffc107', icon: 'warning sign' },
    info: { bg: '#d1ecf1', border: '#17a2b8', icon: 'info circle' },
};

const DashboardNudges = ({ assessments, streakData, goals, onStartRecording, userId }) => {
    const [dismissedIds, setDismissedIds] = useState(() => Object.keys(getDismissedNudges(userId)));

    const nudges = useMemo(() => {
        const all = generateNudges(assessments, streakData, goals);
        return all.filter(n => !dismissedIds.includes(n.id));
    }, [assessments, streakData, goals, dismissedIds]);

    if (nudges.length === 0) return null;

    const handleDismiss = (id) => {
        dismissNudge(userId, id);
        setDismissedIds(prev => [...prev, id]);
    };

    return (
        <div style={{ marginBottom: '20px' }}>
            {nudges.map(nudge => {
                const colors = typeColorMap[nudge.type] || typeColorMap.info;
                return (
                    <Message
                        key={nudge.id}
                        style={{
                            backgroundColor: colors.bg,
                            borderLeft: `4px solid ${colors.border}`,
                            fontFamily: 'Montserrat',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '12px 15px'
                        }}
                        onDismiss={() => handleDismiss(nudge.id)}
                    >
                        <span style={{ fontSize: '20px', marginRight: '12px' }}>{nudge.icon}</span>
                        <div style={{ flex: 1 }}>
                            <strong style={{ fontSize: '14px' }}>{nudge.title}</strong>
                            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#555' }}>{nudge.message}</p>
                        </div>
                        {nudge.actionLabel && nudge.actionType === 'startRecording' && onStartRecording && (
                            <Button
                                size="small"
                                style={{
                                    backgroundColor: colors.border,
                                    color: 'white',
                                    fontFamily: 'Montserrat',
                                    marginLeft: '12px',
                                    whiteSpace: 'nowrap'
                                }}
                                onClick={() => onStartRecording(nudge.sessionType)}
                            >
                                {nudge.actionLabel}
                            </Button>
                        )}
                    </Message>
                );
            })}
        </div>
    );
};

export default DashboardNudges;
